import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonToolbar,
  IonRefresher,
  IonRefresherContent,
  type RefresherCustomEvent,
} from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, type ComponentPropsWithoutRef } from "react";
import { useHistory } from "react-router-dom";
import ChevronRightIcon from "shared/components/icons/chevron/ChevronRightIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import TemplateComponent from "shared/components/template/TemplateComponent";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { getPlatform, Platform } from "shared/types/DeviceInfo";
import { NotificationsModalTrigger } from "hub/src/components/common/modal/notifications/NotificationsModal";
import { useNotificationsContext } from "hub/src/context/NotificationsProvider/NotificationsContext";
import { useBackButtonVisibility } from "hub/src/hooks/routes/useBackButtonVisibility";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";

type PageComponentProps = ComponentPropsWithoutRef<typeof IonPage>;
type PageRefresherProps = {
  disabled?: boolean;
  scrollElementSelector?: string;
};
type PageContentProps = ComponentPropsWithoutRef<"div"> & {
  refresherDisabled?: boolean;
  refresherScrollElementSelector?: string;
};
type PageHeaderButtonsProps = {
  showBackButton?: boolean;
  backButtonUrl?: string;
  buttonSlotStart?: React.ReactNode;
  buttonSlotEnd?: React.ReactNode;
};
type PageHeaderProps = ComponentPropsWithoutRef<typeof IonHeader> & {
  content?: React.ReactNode;
} & PageHeaderButtonsProps;

const PageHeaderStartButton: React.FC<PageHeaderButtonsProps> = ({
  showBackButton,
  backButtonUrl,
  buttonSlotStart,
}) => {
  const history = useHistory();
  const customRouter = useCustomRouter();
  if (buttonSlotStart) {
    return <>{buttonSlotStart}</>;
  }

  if (showBackButton) {
    return (
      <PillarForm.Button
        testid="page-header-back-button"
        className="rounded-full bg-neutral-light !size-6 flex justify-center items-center group"
        onClick={() => {
          if (backButtonUrl) {
            customRouter.push(backButtonUrl);
          } else {
            history.goBack();
          }
        }}
      >
        <ChevronRightIcon className="h-3 w-3 stroke-neutral-mid-600 rotate-180 group-hover:stroke-neutral-mid-900" />
      </PillarForm.Button>
    );
  }

  return <IonMenuButton data-testid="page-header-menu-button" />;
};

const PageHeaderEndButton: React.FC<PageHeaderButtonsProps> = ({
  buttonSlotEnd,
}) => {
  const { notificationsDisabled } = useNotificationsContext();

  if (buttonSlotEnd) {
    return <>{buttonSlotEnd}</>;
  }

  if (!notificationsDisabled) {
    return <NotificationsModalTrigger />;
  }

  // Invisible element to reserve space for the button
  return <IonMenuButton className="invisible pointer-events-none" />;
};

const PageHeader = (props: PageHeaderProps) => {
  const {
    content,
    showBackButton,
    backButtonUrl,
    buttonSlotStart,
    buttonSlotEnd,
    ...rest
  } = props;

  const buttonsProps: PageHeaderButtonsProps = {
    showBackButton: useBackButtonVisibility({ showBackButton }),
    backButtonUrl,
    buttonSlotStart,
    buttonSlotEnd,
  };

  const device = useDeviceInfo();
  const session = useCustomSession();

  const hubHeadingTemplate =
    session.society?.societySettingsPublic?.templates?.hubHeadingTemplate ?? "";
  const hubMobileAppHeadingTemplate =
    session.society?.societySettingsPublic?.templates
      ?.hubMobileAppHeadingTemplate ?? "";
  const headingTemplate = device.isNativeApp
    ? hubMobileAppHeadingTemplate
    : hubHeadingTemplate;

  const toolbarRef = useRef<HTMLIonToolbarElement>(null);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML =
      ".toolbar-container { overflow: visible!important; contain: none!important; }";
    toolbarRef.current?.shadowRoot?.appendChild(style);
  }, [toolbarRef.current]);

  return (
    <IonHeader className="shadow " {...rest}>
      <IonToolbar ref={toolbarRef} className="!overflow-visible !contain-none">
        {content || (
          <TemplateComponent
            template={headingTemplate}
            data={session.society!}
          />
        )}

        <IonButtons slot="start" className="!w-6">
          <PageHeaderStartButton {...buttonsProps} />
        </IonButtons>

        <IonButtons slot="end" className="!w-6">
          <PageHeaderEndButton {...buttonsProps} />
        </IonButtons>
      </IonToolbar>
    </IonHeader>
  );
};

type RefresherConfig = {
  refresherProps?: ComponentPropsWithoutRef<typeof IonRefresher>;
  refresherContentProps?: ComponentPropsWithoutRef<typeof IonRefresherContent>;
};

const REFRESHER_PLATFORM_CONFIG_RECORD: Partial<
  Record<Platform, RefresherConfig>
> = {
  [Platform.Android]: {
    refresherProps: { mode: "md" },
    refresherContentProps: {
      pullingIcon: "circular",
      refreshingSpinner: "circular",
    },
  },
  [Platform.iOS]: {
    refresherProps: { mode: "ios" },
    refresherContentProps: {
      pullingIcon: "lines",
      refreshingSpinner: "lines",
    },
  },
};

const PageRefresher = ({
  disabled: externalDisabled = false,
  scrollElementSelector = ".page-content-container",
}: PageRefresherProps) => {
  const device = useDeviceInfo();
  const queryClient = useQueryClient();
  const session = useCustomSession();

  const disabled = externalDisabled || device.isWeb;

  const config = REFRESHER_PLATFORM_CONFIG_RECORD[getPlatform(device)];

  useEffect(() => {
    if (disabled) {
      return;
    }

    const scrollElement = document.querySelector(scrollElementSelector);

    if (!scrollElement) {
      return;
    }

    scrollElement.classList.add("ion-content-scroll-host");

    return () => {
      scrollElement.classList.remove("ion-content-scroll-host");
    };
  }, [disabled, scrollElementSelector]);

  return (
    <IonRefresher
      key={scrollElementSelector}
      disabled={disabled}
      slot="fixed"
      onIonRefresh={async (event: RefresherCustomEvent) => {
        const refresher = event.target as HTMLIonRefresherElement;
        try {
          await session.refreshSession();
          await queryClient.invalidateQueries();
        } finally {
          await refresher.complete();
        }
      }}
      {...config?.refresherProps}
    >
      <IonRefresherContent {...config?.refresherContentProps} />
    </IonRefresher>
  );
};

const PageContent = ({
  refresherDisabled,
  refresherScrollElementSelector,
  children,
  className,
  ...props
}: PageContentProps) => {
  return (
    <IonContent className="h-full w-full" scrollY={false}>
      <PageRefresher
        disabled={refresherDisabled}
        scrollElementSelector={refresherScrollElementSelector}
      />
      {/* TODO: We should be able to just style the IonContent and not have this containg div afaik
      but the styling isn't being respected so we use this containing div to style. */}
      <div
        className={`page-content-container h-full w-full ${className ?? ""}`}
        {...props}
      >
        {children}
      </div>
    </IonContent>
  );
};

const PageComponent = ({
  children,
  className,
  ...props
}: PageComponentProps) => {
  return (
    <IonPage
      className={`page-component h-full w-full ${className ?? ""}`}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          switch (child.type) {
            case PageHeader:
              return child;
            case PageContent:
              return child;
            default:
              return null;
          }
        }
        return null;
      })}
    </IonPage>
  );
};

PageComponent.Header = PageHeader;
PageComponent.Content = PageContent;

export default PageComponent;
